import { CommonModule } from '@angular/common';
import {
	Component,
	DestroyRef,
	InputSignal,
	OnInit,
	OutputEmitterRef,
	Signal,
	ViewChild,
	computed,
	effect,
	input,
	output,
	signal,
	WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLabel } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { Observable, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';

// models
import { IChat, IChatsResponse } from './chat';
import { IOpenChatEvent } from '../../models/chat';
import { RequestParams } from '../../models/requestParams';

// services
import { ChatService } from '../../services/chat.service';
import { SessionService } from '../../services/session.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { SocketService, SocketGroup } from '../../services/socket.service';
import { Utils } from '../../services/utils.service';

// components
import { HumanDatePipe } from '../../pipes/human-date.pipe';
import { HumanNumberPipe } from '../../pipes/human-number.pipe';
import { LoaderComponent } from '../loader/loader.component';
import { MentionParserPipe } from '../../pipes/mention-parser.pipe';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhLoaderComponent } from '../ph-loader/ph-loader.component';
import { PlanogramChatDrawerComponent } from '../planogram-chat-drawer/planogram-chat-drawer.component';
import { SearchComponent } from '../search/search.component';

@Component({
	selector: 'app-all-planogram-chats',
	templateUrl: './all-planogram-chats.component.html',
	styleUrls: ['./all-planogram-chats.component.scss'],
    providers: [ChatService],
    imports: [
        HumanDatePipe,
        CommonModule,
        TranslateModule,
        PhLoaderComponent,
        InfiniteScrollDirective,
        HumanNumberPipe,
        MentionParserPipe,
        PlanogramChatDrawerComponent,
        MatLabel,
        PhIconWrapperComponent,
        PhIconComponent,
        PhButtonComponent,
        MatTooltipModule,
        SearchComponent,
        LoaderComponent
    ],
})
export class AllPlanogramChatsComponent implements OnInit {

    emptySubtitle: InputSignal<string> = input<string>('');
    isCloseIcon: InputSignal<boolean> = input<boolean>(false);
    hideCloseButton: InputSignal<boolean> = input<boolean>(false);
    openEvent: InputSignal<IOpenChatEvent> = input<IOpenChatEvent>(null);
    chats: WritableSignal<Array<IChat>> = signal<Array<IChat>>([]);
    hasChats: WritableSignal<boolean> = signal<boolean>(false);
    isLoading: WritableSignal<boolean> = signal<boolean>(false);
	isLoadingNext: WritableSignal<boolean> = signal<boolean>(false);
    nextUrl: WritableSignal<string> = signal<string>('');
    openedChatName: WritableSignal<string> = signal<string>('');
    totalUnread: WritableSignal<number> = signal<number>(0);
    pinLoadingChats: WritableSignal<Array<number>> = signal<Array<number>>([]);
    searchQuery: WritableSignal<string> = signal<string>('');
	requestParams: RequestParams = new RequestParams({ type: 'PLANOGRAM' });

    close: OutputEmitterRef<void> = output<void>();

	@ViewChild(PlanogramChatDrawerComponent) chatDrawer!: PlanogramChatDrawerComponent;

    displayChats: Signal<Array<IChat>> = computed((): Array<IChat> => this.sortChats(this.chats()));

	pinLoadingChatsSet: Signal<Set<number>> = computed((): Set<number> => new Set(this.pinLoadingChats()));

	private loadSubject: Subject<void> = new Subject<void>();
	private openEventEmitted = false;

	constructor(
        private destroyRef: DestroyRef,
        private socketService: SocketService,
		private chatService: ChatService,
		private sessionService: SessionService,
        private snackBarService: SnackBarService,
        private translate: TranslateService
	) {
		effect((): void => {
			if (!this.isLoading() && !this.openEventEmitted) {
				this.openEventEmitted = true;
				this.sessionService.openChat$.next(this.openEvent());
			}
		});
	}

	ngOnInit(): void {
		this.loadSubject
			.pipe(
				switchMap((): Observable<IChatsResponse> => {
					this.isLoading.set(true);
					return this.chatService.getChats(this.requestParams.getParams());
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe({
				next: (response: IChatsResponse): void => {
					this.chats.set(response?.results ?? []);
                    this.hasChats.set(response.has_data);
					this.nextUrl.set(response?.next ?? null);
					this.isLoading.set(false);
				},
				error: (): void => {
					this.snackBarService.setErrorNotification();
					this.chats.set([]);
					this.nextUrl.set(null);
					this.isLoading.set(false);
				}
			});

		this.loadSubject.next();
		this.socketService.addSubscription(SocketGroup.ChatList);
		
		this.socketService.chatListUnreadCount
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((data: { unread: number }): void => {
				this.totalUnread.set(data.unread);
			});

		this.socketService.chatListUpdate
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((chat: IChat): void => {
				this.updateChatInList(chat);
			});

        this.sessionService.openChat$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: IOpenChatEvent): void => {
                if (Utils.isNullOrUndefined(data?.chatId)) return;
                this.chatService.id = data.chatId;
                
                const chat: IChat = this.chats().find((c: IChat): boolean => c.id === data.chatId);
                if (chat) this.openedChatName.set(chat.name);

                if (this.chatDrawer) this.chatDrawer.onOpen('all_chats', data.focusMessageId);
            });
	}

    private sortChats(chats: Array<IChat>): Array<IChat> {
        const byDatetimeDesc = (a: IChat, b: IChat): number => {
            const aTime: number = new Date(a.datetime).getTime();
            const bTime: number = new Date(b.datetime).getTime();
            return bTime - aTime;
        };
        const uniqueMap = new Map<number, IChat>();
        chats.forEach((chat: IChat): void => {
            uniqueMap.set(chat.id, chat);
        });
        const unique: Array<IChat> = Array.from(uniqueMap.values());
        const pinned: Array<IChat> = unique.filter((c: IChat): boolean => c.is_pinned).sort(byDatetimeDesc);
        const unpinned: Array<IChat> = unique.filter((c: IChat): boolean => !c.is_pinned).sort(byDatetimeDesc);
        return [...pinned, ...unpinned];
    }

	private updateChatInList(updatedChat: IChat): void {
		this.chats.update((currentChats: Array<IChat>): Array<IChat> => {
			const existingIndex: number = currentChats.findIndex((c: IChat): boolean => c.id === updatedChat.id);
			if (existingIndex !== -1) {
				const updated: Array<IChat> = [...currentChats];
				updated[existingIndex] = updatedChat;
				return updated;
			}
			if (this.requestParams.has('search')) {
				return currentChats;
			}
            this.hasChats.set(true);
			return [updatedChat, ...currentChats];
		});
	}

    openHelpdesk(): void {
        const links = {
            en: 'https://helpdesk.planohero.com/en/article/chat-web-version-8o1snl/',
            uk: 'https://helpdesk.planohero.com/uk/article/chat-veb-versiya-sxrtg8/',
            ru: 'https://helpdesk.planohero.com/ru/article/chat-veb-versiya-tze0rn/'
        };

        window.open(links[this.translate.getCurrentLang()] ?? links.en, '_blank');
    }

	search(value: string): void {
		this.searchQuery.set(value);
		this.requestParams.updateParams({ search: value });
		this.loadSubject.next();
	}

	loadNextChats(): void {
		const next: string = this.nextUrl();
		if (!next || this.isLoadingNext()) return;

        this.isLoadingNext.set(true);
		this.chatService.loadNextChats(next)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response: IChatsResponse): void => {
                    if (response?.results?.length > 0) {
                        this.chats.update((current: Array<IChat>): Array<IChat> => [...current, ...response.results]);
                        this.nextUrl.set(response.next);
                    } else {
                        this.nextUrl.set(null);
                    }
                    this.isLoadingNext.set(false);
                },
                error: (): void => {
                    this.isLoadingNext.set(false);
                }
            });
	}

    onClose(): void {
        this.close.emit();
    }

	openChat(chat: IChat): void {
		this.chatService.id = chat.id;
		this.openedChatName.set(chat.name);
		if (this.chatDrawer) {
			this.chatDrawer.onOpen('all_chats');
		}
	}

	onChatDrawerClose(): void {
		this.openedChatName.set('');
        this.chatService.id = null;
        this.chatService.focusMessageId.set(null);
        this.chatService.tempMessage = null;
	}

    toggleChatPin(chat: IChat): void {
        if (this.pinLoadingChatsSet().has(chat.id)) return;
        const updatedChat: IChat = { ...chat, is_pinned: !chat.is_pinned };

        this.pinLoadingChats.update((current: Array<number>): Array<number> => [...current, updatedChat.id]);
        this.chats.update((currentChats: Array<IChat>): Array<IChat> => {
            const existingIndex: number = currentChats.findIndex((c: IChat): boolean => c.id === updatedChat.id);
            if (existingIndex !== -1) {
                const filtered: Array<IChat> = currentChats.filter((c: IChat): boolean => c.id !== updatedChat.id);
                return [updatedChat, ...filtered];
            }
            return currentChats;
        });

        this.chatService.updateChat(updatedChat.id, { is_pinned: updatedChat.is_pinned }).subscribe({
            next: (): void => {
                this.pinLoadingChats.update((current: Array<number>): Array<number> => current.filter((id: number): boolean => id !== updatedChat.id));
            },
            error: (): void => {
                this.snackBarService.setErrorNotification();
                this.pinLoadingChats.update((current: Array<number>): Array<number> => current.filter((id: number): boolean => id !== updatedChat.id));
            }
        });
	}
}